import { useDispatch } from 'react-redux'
import { updateFilter } from '../../store/actions/filter.actions'
import { updateSkip } from '../../store/actions/datatable.actions'
import {
  getStudentsForAssessment as getStudentsForAssessmentService,
  getStudentIdsForAssessment as getStudentIdsForAssessmentService,
  getClassesForAssessment as getClassesForAssessmentService,
  getFullAssessmentData as getFullAssessmentDataService
} from '../../services/data-entry/assessments.service'
import { useSnackbar } from 'notistack'
import { useCallback } from 'react'
import { handlerErrorV2 } from '../../utils/errors'
import {
  toggleLoadingAssessmentsBuilderListClasses,
  toggleLoadingAssessmentsBuilderListStudents,
  updateAssessmentsBuilderListClasses,
  updateAssessmentsBuilderListStudents,
  updateAssessmentBuilder
} from '../../store/actions/dataEntryAssessmentBuilderActions'
import {
  ASSESSMENT_TYPE_STANDARD,
  ASSESSMENT_TYPE_CUSTOM
} from '../../components/Dashboard/DataEntry/constants/typeAssessment'

const useAssessment = () => {
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()

  const getStudentsForAssessment = useCallback(
    async (skip, rowsPerPage, filter, assessmentType = ASSESSMENT_TYPE_STANDARD) => {
      if (assessmentType === ASSESSMENT_TYPE_CUSTOM) {
        const isTeacher =
          Array.isArray(filter?.rolesId) && filter.rolesId.length === 1 && filter.rolesId[0] === 'Teacher'
        // MF-532: permitir consulta school-wide por flag (ej. "todos los EAL de
        // la escuela") sin exigir year level. Antes se cortaba aquí y el caso de
        // uso del ticket no cargaba nada.
        const hasFlagFilter = Array.isArray(filter?.flag) ? filter.flag.length > 0 : !!filter?.flag
        if (!isTeacher && !filter?.yearLevel?.length && !hasFlagFilter) {
          dispatch(updateAssessmentsBuilderListStudents({ data: [], count: 0 }))
          return
        }
      } else if (assessmentType === ASSESSMENT_TYPE_STANDARD) {
        if (!filter?.classId || filter?.classId.length === 0) {
          dispatch(updateAssessmentsBuilderListStudents({ data: [], count: 0 }))
          return
        }
      } else {
        return // If the assessment type is not recognized, do nothing
      }

      dispatch(toggleLoadingAssessmentsBuilderListStudents(true))
      try {
        const filterData = { ...filter }
        if (filterData.yearLevelId) {
          filterData.yearLevel = filterData.yearLevelId
          delete filterData.yearLevelId
        }
        const response = await getStudentsForAssessmentService(skip, rowsPerPage, filterData)
        if (!response?.data && response?.error) {
          enqueueSnackbar(response.error.message || 'An error ocurred', {
            variant: 'error'
          })
        } else {
          dispatch(updateAssessmentsBuilderListStudents(response?.data || { data: [], count: 0 }))
        }
      } catch (error) {
        const message = handlerErrorV2(error)
        dispatch(updateAssessmentsBuilderListStudents({ data: [], count: 0 }))
        enqueueSnackbar(message, { variant: 'error' })
      } finally {
        dispatch(toggleLoadingAssessmentsBuilderListStudents(false))
      }
    },
    []
  )

  // MF-532: ids del set filtrado completo, para el "select all" (CUSTOM). Se
  // piden on-demand en vez de venir en cada carga de la lista paginada. Devuelve
  // un array de strings (ids); pasa el filtro base SIN show/included/excluded.
  const getStudentIdsForAssessment = useCallback(async (filter) => {
    try {
      const filterData = { ...filter }
      if (filterData.yearLevelId) {
        filterData.yearLevel = filterData.yearLevelId
        delete filterData.yearLevelId
      }
      const response = await getStudentIdsForAssessmentService(filterData)
      if (!response?.data && response?.error) {
        enqueueSnackbar(response.error.message || 'An error ocurred', {
          variant: 'error'
        })
        return []
      }
      return Array.isArray(response?.data) ? response.data : []
    } catch (error) {
      const message = handlerErrorV2(error)
      enqueueSnackbar(message, { variant: 'error' })
      return []
    }
  }, [])

  const getClassesForAssessment = useCallback(async (filter) => {
    if (!filter?.yearLevel || filter?.yearLevel.length === 0) {
      dispatch(updateAssessmentsBuilderListClasses({ data: [], count: 0 }))
      return
    }

    dispatch(toggleLoadingAssessmentsBuilderListClasses(true))
    try {
      const filterData = { ...filter }
      const response = await getClassesForAssessmentService(filterData)
      if (!response?.data && response?.error) {
        enqueueSnackbar(response.error.message || 'An error ocurred', {
          variant: 'error'
        })
      } else {
        const resp = response?.data
        const data = {
          data:
            resp.map((el) => ({
              ...el,
              id: el._id,
              value: el._id,
              label: el.code
            })) || [],
          count: resp?.length
        }
        dispatch(updateAssessmentsBuilderListClasses(data))
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      dispatch(updateAssessmentsBuilderListClasses({ data: [], count: 0 }))
      enqueueSnackbar(message, { variant: 'error' })
    } finally {
      dispatch(toggleLoadingAssessmentsBuilderListClasses(false))
    }
  }, [])

  const getFullAssessmentData = useCallback(async (assessmentId = '', filter) => {
    if (!assessmentId) {
      return
    }

    try {
      const filterData = { ...filter }
      const response = await getFullAssessmentDataService(assessmentId, filterData)
      if (!response?.data && response?.error) {
        enqueueSnackbar(response.error.message || 'An error ocurred', {
          variant: 'error'
        })
      } else {
        const resp = response?.data
        const checkedIds = resp?.students?.map((el) => el._id) || []
        const template = resp?.template
          ? { ...resp?.template, value: resp?.template._id, label: resp?.template?.title }
          : null
        const classes =
          resp?.classes?.map((el) => ({ ...el, id: el._id, value: el._id, label: el.code })) || []

        const studentsWithMarking = resp?.students
          ?.filter((el) => el?.hasMarking === 1)
          ?.map((el) => {
            return {
              ...el
            }
          })

        dispatch(updateFilter('assessmentStudentsList', {}))
        dispatch(updateSkip('assessmentStudentsList', 0))
        dispatch(
          updateAssessmentBuilder({
            name: resp?.name || '',
            notes: resp?.notes || '',
            sittingDate: resp?.sittingDate || null,
            dueDate: resp?.dueDate || null,
            error: null,
            template,
            checkedIds,
            studentsWithMarking,
            round: resp?.round || '',
            rounds: resp?.rounds || [],
            carryForward: resp?.carryForward ?? true,
            type: resp?.type || '',
            reviewers: resp?.reviewers || [],
            participantRoles: resp?.participantRoles || ['Student'],
            class: {
              filteredClasses: [],
              selectedClasses: classes
            }
          })
        )
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      enqueueSnackbar(message, { variant: 'error' })
    }
  }, [])

  return {
    getStudentsForAssessment,
    getStudentIdsForAssessment,
    getClassesForAssessment,
    getFullAssessmentData
  }
}

export default useAssessment
